import Route from '@ember/routing/route'
import { SchoolClassOptionTypes } from 'teacher-ui-client/components/base/select/school-class/options'
import type Store from '@ember-data/store'
import type SessionService from 'teacher-ui-client/services/session'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import { isPresent } from '@ember/utils'

interface ClassQueryFilter {
  has_classes?: boolean
  teacher_ids?: string[]
  student_ids?: string[]
}

/**
 * Determine the filter to use in query requests for /shares based on the selected class option
 */
export function sharesClassQueryFilter(
  schoolClassId: string | SchoolClassOptionTypes,
  store: Store,
  session: SessionService,
): ClassQueryFilter {
  if (schoolClassId === SchoolClassOptionTypes.All.toString()) {
    return {} // not a supported selectable option
  } else if (schoolClassId === SchoolClassOptionTypes.My.toString()) {
    const currentTeacherId = session.currentTeacher.id
    return { teacher_ids: currentTeacherId ? [currentTeacherId] : [] }
  } else {
    const student_ids =
      store
        .peekRecord<SchoolClass>('school-class', schoolClassId)
        // @ts-expect-error - something wrong with relationship reference types
        ?.hasMany<Student, 'students'>('students')
        .ids()
        .filter(isPresent) ?? []
    return { student_ids }
  }
}

/**
 * Determine the filter to use in query requests for /student-stats based on the selected class option
 * has_classes will filter returned students by reading classes.
 */
export function studentStatsClassQueryFilter(
  schoolClassId: string | SchoolClassOptionTypes,
  store: Store,
  session: SessionService,
): ClassQueryFilter {
  if (schoolClassId === SchoolClassOptionTypes.All.toString()) {
    return { has_classes: true }
  } else if (schoolClassId === SchoolClassOptionTypes.My.toString()) {
    const currentTeacherId = session.currentTeacher.id
    return { has_classes: true, teacher_ids: currentTeacherId ? [currentTeacherId] : [] }
  } else {
    const student_ids =
      store
        .peekRecord<SchoolClass>('school-class', schoolClassId)
        // @ts-expect-error - something wrong with relationship reference types
        ?.hasMany<Student, 'students'>('students')
        .ids()
        .filter(isPresent) ?? []
    return { student_ids }
  }
}

export default class extends Route {
  /**
   * These QPs are defined here on the index route, so that they stay selected when the user navigates between the
   * Current/Past/MyStudents/AllStudents tabs (the by-collection and by-students routes)
   *
   * However, note that the "all-classes" school class option is not supported on all tabs..
   */
  queryParams = {
    schoolClassId: {
      replace: true,
    },
    search: {
      replace: true,
    },
  }

  model() {
    const { subscriptionType } = this.modelFor('subscription-type') as ModelForSubscriptionTypeRoute
    return { subscriptionType }
  }
}
